// ─────────────────────────────────────────────
// Oike Diagnostic — CRM export metrics
// The export is read in the browser and never uploaded. computeCrmMetrics()
// turns its rows into numbers; only that result (plus the seller ↔ participant
// links) is stored, after sanitizeCrmMetrics() rebuilds it on the server from
// known keys only. Pure functions: no I/O.
// ─────────────────────────────────────────────
import { CRM_COLUMNS, STALLED_DAYS } from './diagnosticConfig.js';

const DAY = 86_400_000;
export const CRM_METRICS_VERSION = 1;
const MAX_GROUPS = 30;   // stages, channels and loss reasons kept
const MAX_LABEL = 120;

// ── Parsing ──

const str = v => (v === null || v === undefined ? '' : String(v).trim());

/** Amount from a cell: numbers pass; text uses the chosen decimal separator. */
export function parseAmount(v, decimal = '.') {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  let s = str(v).replace(/[^\d.,-]/g, '');
  if (!s) return null;
  s = decimal === ',' ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Date from a cell → UTC ms at midnight, or null. format: 'dmy' | 'mdy' (ISO always works). */
export function parseDate(v, format = 'dmy') {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : Date.UTC(v.getFullYear(), v.getMonth(), v.getDate());
  if (typeof v === 'number') { // Excel serial date
    if (v < 1 || v > 100000) return null;
    return Math.round((v - 25569)) * DAY;
  }
  const s = str(v);
  if (!s) return null;
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  let y, mo, d;
  if (m) [, y, mo, d] = m.map(Number);
  else {
    m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/);
    if (!m) return null;
    const [a, b, c] = [Number(m[1]), Number(m[2]), Number(m[3])];
    y = c < 100 ? 2000 + c : c;
    [d, mo] = format === 'mdy' ? [b, a] : [a, b];
  }
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const t = Date.UTC(y, mo - 1, d);
  return Number.isNaN(t) ? null : t;
}

/** Suggested result for a raw value of the result column. */
export function guessResult(value) {
  const s = str(value).toLowerCase();
  if (/won|ganad|cerrad[ao] ganad|success|exito|éxito/.test(s)) return 'won';
  if (/lost|perdid|descartad|rechazad|cancel/.test(s)) return 'lost';
  return 'open';
}

// ── Stage order ──

/** A final stage: closed, won, lost, signed… (it can only come last in the funnel). */
export function isClosingStage(value) {
  const s = str(value).toLowerCase();
  return guessResult(s) !== 'open' || /cerrad|closed|firmad|signed|finaliz|terminad/.test(s);
}

// Typical order of open stages; a stage that matches none goes in the middle, by frequency
const STAGE_HINTS = [
  /lead|prospect|nuev|new|entrante|inbound/, /contact|primer/, /calific|qualif|discovery|descubr|diagn/,
  /reuni|meeting|demo|presentaci/, /propuesta|proposal|cotiz|quote|oferta/, /negoci/, /cierre|closing|contrat|commit|aprobaci/,
];
const hintRank = (v) => { const s = str(v).toLowerCase(); const i = STAGE_HINTS.findIndex(re => re.test(s)); return i < 0 ? 3.5 : i; };

/** A logical default order: open stages by typical position (then frequency), final stages last. */
export function suggestStageOrder(values) {
  const indexed = values.map((v, i) => ({ v, i }));
  const rank = (x) => (isClosingStage(x.v) ? 100 : hintRank(x.v));
  return indexed.sort((a, b) => rank(a) - rank(b) || a.i - b.i).map(x => x.v);
}

/** Final stages placed before another stage of the funnel (they should be last). */
export function stageOrderWarnings(stageOrder) {
  return stageOrder.filter((v, i) => isClosingStage(v) && stageOrder.slice(i + 1).some(x => !isClosingStage(x)));
}

/** Column suggestions from the headers of the file. */
export function guessMapping(headers) {
  const mapping = {};
  const used = new Set();
  for (const col of CRM_COLUMNS) {
    const h = headers.find(x => !used.has(x) && col.guess.test(x));
    if (h) { mapping[col.key] = h; used.add(h); }
  }
  mapping.qualification = [];
  return mapping;
}

const normalizeName = s => str(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ');

/** Suggested participant for each CRM seller name (exact name, then first + last name). */
export function guessSellerLinks(sellerNames, participants) {
  const out = {};
  for (const name of sellerNames) {
    const n = normalizeName(name);
    const exact = participants.find(p => normalizeName(p.name) === n);
    const partial = exact || participants.find(p => {
      const parts = normalizeName(p.name).split(' ');
      return parts.length > 1 && n.includes(parts[0]) && n.includes(parts[parts.length - 1]);
    });
    out[name] = partial ? partial.id : null;
  }
  return out;
}

/** Distinct non-empty values of a column, most frequent first. */
export function distinctValues(rows, column) {
  const counts = new Map();
  for (const r of rows) { const v = str(r[column]); if (v) counts.set(v, (counts.get(v) || 0) + 1); }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([v]) => v);
}

// ── Metrics ──

const round = (n, d = 2) => (n === null || !Number.isFinite(n) ? null : Math.round(n * 10 ** d) / 10 ** d);
const avg = list => (list.length ? list.reduce((a, b) => a + b, 0) / list.length : null);

function bucket(opps, stageOrder, ctx) {
  const won = opps.filter(o => o.result === 'won');
  const lost = opps.filter(o => o.result === 'lost');
  const open = opps.filter(o => o.result === 'open');
  const k = stageOrder.length;

  // Stage conversion from a snapshot: "reached" is the furthest stage; won deals
  // reached every stage. Open deals sitting in a stage are still undecided, so they
  // are left out of that stage's base. Lost deals without a known stage count as
  // lost in the first stage (and the metric is marked estimated).
  const reached = o => (o.result === 'won' ? k : o.stageIndex ?? 0);
  const stageConversion = stageOrder.map((from, i) => {
    const base = opps.filter(o => reached(o) >= i && !(o.result === 'open' && reached(o) === i));
    const passed = base.filter(o => reached(o) >= i + 1);
    return { from, to: i + 1 < k ? stageOrder[i + 1] : 'won', rate: base.length ? round(passed.length / base.length, 4) : null, base: base.length };
  });

  const stalled = open.filter(o => {
    const ref = o.lastModifiedAt ?? o.createdAt;
    return ref !== null && (ctx.exportDate - ref) / DAY > STALLED_DAYS;
  });

  const cycles = won.filter(o => o.createdAt !== null && o.closedAt !== null && o.closedAt >= o.createdAt)
    .map(o => (o.closedAt - o.createdAt) / DAY);
  const tickets = won.map(o => o.amount).filter(a => a !== null);
  const openAmounts = open.map(o => o.amount).filter(a => a !== null);

  let qualificationComplete = null;
  if (ctx.hasQualification) {
    qualificationComplete = open.length ? round(open.filter(o => o.qualified).length / open.length, 4) : null;
  }

  return {
    opportunities: opps.length, open: open.length, won: won.length, lost: lost.length,
    winRate: won.length + lost.length ? round(won.length / (won.length + lost.length), 4) : null,
    avgTicket: round(avg(tickets)),
    avgCycleDays: round(avg(cycles), 1),
    // Value of the open pipeline (opportunities with an amount), for the KPIs of System Design
    openAmount: openAmounts.length ? round(openAmounts.reduce((a, b) => a + b, 0)) : null,
    stalled: { count: stalled.length, rate: open.length ? round(stalled.length / open.length, 4) : null },
    stageConversion,
    qualificationComplete,
  };
}

// ── Segments (Fase 7): by industry and client size, for the ICP of System Design ──

export const NO_SEGMENT = '(sin dato)';

/** A client size from a cell: a head count becomes the landing's ranges; text is kept as written. */
export function normalizeSize(v) {
  const s = str(v);
  if (!s) return '';
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return n <= 10 ? '1-10' : n <= 50 ? '11-50' : n <= 200 ? '51-200' : n <= 500 ? '201-500' : '501+';
  }
  return s.slice(0, MAX_LABEL);
}

/** The slim bucket of a segment: no stage conversion, so many segments stay small. */
function segmentBucket(opps) {
  const won = opps.filter(o => o.result === 'won');
  const lost = opps.filter(o => o.result === 'lost');
  const tickets = won.map(o => o.amount).filter(a => a !== null);
  const cycles = won.filter(o => o.createdAt !== null && o.closedAt !== null && o.closedAt >= o.createdAt).map(o => (o.closedAt - o.createdAt) / DAY);
  return {
    opportunities: opps.length, open: opps.filter(o => o.result === 'open').length, won: won.length, lost: lost.length,
    winRate: won.length + lost.length ? round(won.length / (won.length + lost.length), 4) : null,
    avgTicket: round(avg(tickets)), avgCycleDays: round(avg(cycles), 1),
  };
}

function groupBy(opps, keyOf) {
  const groups = new Map();
  for (const o of opps) { const k = keyOf(o); (groups.get(k) || groups.set(k, []).get(k)).push(o); }
  return [...groups.entries()].sort((a, b) => b[1].length - a[1].length).slice(0, MAX_GROUPS);
}

/**
 * The three largest open opportunities, for the "Cuentas" module. The ONLY place where something
 * of a row is kept (documented in airtable-schema.md): company name, amount, stage, age in days
 * and the seller as a participant id. No contacts, no dates, no names of sellers. It is
 * deleted with the diagnostic.
 */
function topOpenOf(opps, sellerLinks, exportDate) {
  return opps.filter(o => o.result === 'open' && o.amount !== null && o.amount > 0)
    .sort((a, b) => b.amount - a.amount || (a.createdAt ?? Infinity) - (b.createdAt ?? Infinity))
    .slice(0, 3)
    .map(o => ({
      company: o.company || null, amount: o.amount, stage: o.stage || null,
      ageDays: o.createdAt !== null ? Math.max(0, Math.floor((exportDate - o.createdAt) / DAY)) : null,
      sellerId: sellerLinks[o.seller] || null,
    }));
}

/**
 * rows: objects keyed by header. mapping: { seller, stage, ..., qualification: [headers] }.
 * options: { dateFormat, decimal, resultMap: {raw: 'won'|'lost'|'open'}, stageOrder: [raw stage],
 *            sellerLinks: {crmName: participantId|null}, exportDate: 'YYYY-MM-DD' }
 */
export function computeCrmMetrics(rows, mapping, options) {
  const { dateFormat = 'dmy', decimal = '.', resultMap = {}, sellerLinks = {} } = options;
  const stageOrder = (options.stageOrder || []).slice(0, MAX_GROUPS);
  const exportDate = parseDate(options.exportDate, 'dmy') ?? Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate());
  const quals = (mapping.qualification || []).filter(Boolean);
  const has = key => !!mapping[key];

  const opps = [];
  let skipped = 0;
  let lostWithoutStage = 0;
  for (const r of rows) {
    const seller = str(r[mapping.seller]);
    const rawResult = str(r[mapping.result]);
    const stage = str(r[mapping.stage]);
    if (!seller && !stage && !rawResult) { skipped++; continue; }
    const result = resultMap[rawResult] || 'open';
    let stageIndex = stageOrder.indexOf(stage);
    if (result === 'lost') {
      const lostAt = has('lostStage') ? stageOrder.indexOf(str(r[mapping.lostStage])) : -1;
      if (lostAt >= 0) stageIndex = lostAt;
      if (stageIndex < 0) lostWithoutStage++;
    }
    opps.push({
      seller, result, stage: stage.slice(0, MAX_LABEL),
      company: has('company') ? str(r[mapping.company]).slice(0, MAX_LABEL) : '',
      industry: has('industry') ? str(r[mapping.industry]).slice(0, MAX_LABEL) || NO_SEGMENT : null,
      size: has('clientSize') ? normalizeSize(r[mapping.clientSize]) || NO_SEGMENT : null,
      stageIndex: stageIndex >= 0 ? stageIndex : null,
      amount: parseAmount(r[mapping.amount], decimal),
      createdAt: parseDate(r[mapping.createdAt], dateFormat),
      closedAt: parseDate(r[mapping.closedAt], dateFormat),
      lastModifiedAt: has('lastModifiedAt') ? parseDate(r[mapping.lastModifiedAt], dateFormat) : null,
      source: has('source') ? str(r[mapping.source]).slice(0, MAX_LABEL) || '(sin origen)' : null,
      lossReason: has('lossReason') ? str(r[mapping.lossReason]).slice(0, MAX_LABEL) : null,
      qualified: quals.length ? quals.every(h => str(r[h]) !== '') : false,
    });
  }

  const ctx = { exportDate, hasQualification: quals.length > 0 };
  const byPerson = {};
  const unlinkedOpps = [];
  const groups = new Map();
  for (const o of opps) {
    const pid = sellerLinks[o.seller];
    if (pid) (groups.get(pid) || groups.set(pid, []).get(pid)).push(o);
    else unlinkedOpps.push(o);
  }
  for (const [pid, list] of groups) byPerson[pid] = bucket(list, stageOrder, ctx);

  let byChannel = null;
  if (has('source')) {
    const ch = new Map();
    for (const o of opps) (ch.get(o.source) || ch.set(o.source, []).get(o.source)).push(o);
    byChannel = Object.fromEntries([...ch.entries()].sort((a, b) => b[1].length - a[1].length).slice(0, MAX_GROUPS)
      .map(([source, list]) => [source, bucket(list, stageOrder, ctx)]));
  }

  let lossReasons = null;
  if (has('lossReason')) {
    const counts = new Map();
    for (const o of opps) if (o.result === 'lost') { const k = o.lossReason || '(sin motivo)'; counts.set(k, (counts.get(k) || 0) + 1); }
    lossReasons = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, MAX_GROUPS).map(([reason, count]) => ({ reason, count }));
  }

  // By industry and by client size (and both together): only with the optional columns
  const seg = (keyOf) => Object.fromEntries(groupBy(opps, keyOf).map(([label, list]) => [label, segmentBucket(list)]));
  const byIndustry = has('industry') ? seg(o => o.industry) : null;
  const bySize = has('clientSize') ? seg(o => o.size) : null;
  const bySegment = has('industry') && has('clientSize')
    ? groupBy(opps, o => `${o.industry}\u0000${o.size}`).map(([k, list]) => { const [industry, size] = k.split('\u0000'); return { industry, size, ...segmentBucket(list) }; })
    : null;

  // By person and origin (Fase 7): which channel each seller uses most and where they win. Linked sellers only.
  let byPersonChannel = null;
  if (has('source')) {
    byPersonChannel = {};
    for (const [pid, list] of groups) byPersonChannel[pid] = Object.fromEntries(groupBy(list, o => o.source).map(([source, l]) => [source, segmentBucket(l)]));
  }

  const missingOptional = CRM_COLUMNS.filter(c => !c.required && !has(c.key)).map(c => c.key);
  return {
    version: CRM_METRICS_VERSION,
    computedAt: new Date().toISOString(),
    exportDate: new Date(exportDate).toISOString().slice(0, 10),
    rows: { total: rows.length, used: opps.length, skipped },
    missingOptional,
    stageOrder,
    estimated: {
      stageConversion: lostWithoutStage > 0,
      stalled: !has('lastModifiedAt'),
    },
    sellerLinks: Object.fromEntries(Object.entries(sellerLinks).slice(0, 200).map(([k, v]) => [String(k).slice(0, MAX_LABEL), v || null])),
    total: bucket(opps, stageOrder, ctx),
    byPerson,
    unlinked: unlinkedOpps.length ? bucket(unlinkedOpps, stageOrder, ctx) : null,
    byChannel,
    lossReasons,
    byIndustry, bySize, bySegment, byPersonChannel,
    topOpen: topOpenOf(opps, sellerLinks, exportDate),
  };
}

/**
 * A CRM imported before Fase 7 has none of the System Design data (the 3 largest open
 * opportunities, the pipeline value, the channel per person): re-importing the same export brings them.
 */
export const isCrmOutdated = (m) => !!m && typeof m === 'object' && !Array.isArray(m.topOpen);

// ── Server-side sanitizing ──
// Rebuilds the metrics from known keys only, so nothing else (rows, names of
// clients, free text) can be stored through this field.

const num = (v, { int = false, min = 0, max = 1e12 } = {}) =>
  (typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max && (!int || Number.isInteger(v)) ? v : null);
const label = v => (typeof v === 'string' && v.length <= MAX_LABEL ? v : null);

function cleanBucket(b, stageCount) {
  if (!b || typeof b !== 'object' || Array.isArray(b)) return null;
  const int = v => num(v, { int: true, max: 1e7 });
  const rate = v => num(v, { max: 1 });
  const conv = Array.isArray(b.stageConversion) ? b.stageConversion.slice(0, stageCount).map(c => ({
    from: label(c?.from), to: label(c?.to), rate: rate(c?.rate), base: int(c?.base) ?? 0,
  })) : [];
  return {
    opportunities: int(b.opportunities) ?? 0, open: int(b.open) ?? 0, won: int(b.won) ?? 0, lost: int(b.lost) ?? 0,
    winRate: rate(b.winRate), avgTicket: num(b.avgTicket), avgCycleDays: num(b.avgCycleDays, { max: 1e5 }),
    openAmount: num(b.openAmount),
    stalled: { count: int(b.stalled?.count) ?? 0, rate: rate(b.stalled?.rate) },
    stageConversion: conv,
    qualificationComplete: rate(b.qualificationComplete),
  };
}

function cleanSegment(b) {
  if (!b || typeof b !== 'object' || Array.isArray(b)) return null;
  const int = v => num(v, { int: true, max: 1e7 });
  return {
    opportunities: int(b.opportunities) ?? 0, open: int(b.open) ?? 0, won: int(b.won) ?? 0, lost: int(b.lost) ?? 0,
    winRate: num(b.winRate, { max: 1 }), avgTicket: num(b.avgTicket), avgCycleDays: num(b.avgCycleDays, { max: 1e5 }),
  };
}

/**
 * participantIds: ids allowed in byPerson / sellerLinks (the diagnostic's participants).
 * Returns the clean object, or null when the input is not CRM metrics.
 */
export function sanitizeCrmMetrics(m, participantIds) {
  if (!m || typeof m !== 'object' || Array.isArray(m) || m.version !== CRM_METRICS_VERSION) return null;
  const allowed = new Set(participantIds);
  const stageOrder = Array.isArray(m.stageOrder) ? m.stageOrder.slice(0, MAX_GROUPS).map(label).filter(x => x !== null) : [];
  const total = cleanBucket(m.total, stageOrder.length);
  if (!total) return null;
  const byPerson = {};
  for (const [pid, b] of Object.entries(m.byPerson || {})) {
    if (!allowed.has(pid)) continue;
    const c = cleanBucket(b, stageOrder.length);
    if (c) byPerson[pid] = c;
  }
  const sellerLinks = {};
  for (const [name, pid] of Object.entries(m.sellerLinks || {}).slice(0, 200)) {
    if (label(name) === null) continue;
    sellerLinks[name] = pid && allowed.has(pid) ? pid : null;
  }
  let byChannel = null;
  if (m.byChannel && typeof m.byChannel === 'object') {
    byChannel = {};
    for (const [source, b] of Object.entries(m.byChannel).slice(0, MAX_GROUPS)) {
      const c = label(source) !== null && cleanBucket(b, stageOrder.length);
      if (c) byChannel[source] = c;
    }
  }
  const lossReasons = Array.isArray(m.lossReasons)
    ? m.lossReasons.slice(0, MAX_GROUPS).map(x => ({ reason: label(x?.reason), count: num(x?.count, { int: true, max: 1e7 }) }))
      .filter(x => x.reason !== null && x.count !== null)
    : null;
  const segmentMap = (x) => {
    if (!x || typeof x !== 'object' || Array.isArray(x)) return null;
    const out = {};
    for (const [k, b] of Object.entries(x).slice(0, MAX_GROUPS)) { const c = label(k) !== null && cleanSegment(b); if (c) out[k] = c; }
    return out;
  };
  const bySegment = Array.isArray(m.bySegment)
    ? m.bySegment.slice(0, MAX_GROUPS).map(x => ({ industry: label(x?.industry), size: label(x?.size), ...cleanSegment(x) }))
      .filter(x => x.industry !== null && x.size !== null && x.opportunities !== undefined)
    : null;
  let byPersonChannel = null;
  if (m.byPersonChannel && typeof m.byPersonChannel === 'object' && !Array.isArray(m.byPersonChannel)) {
    byPersonChannel = {};
    for (const [pid, channels] of Object.entries(m.byPersonChannel)) {
      if (!allowed.has(pid)) continue;
      const c = segmentMap(channels);
      if (c) byPersonChannel[pid] = c;
    }
  }
  // The only row data kept: the 3 largest open opportunities (company, amount, stage, age, seller as participant id)
  const topOpen = Array.isArray(m.topOpen)
    ? m.topOpen.slice(0, 3).map(x => ({
      company: label(x?.company), amount: num(x?.amount), stage: label(x?.stage), ageDays: num(x?.ageDays, { int: true, max: 1e5 }),
      sellerId: typeof x?.sellerId === 'string' && allowed.has(x.sellerId) ? x.sellerId : null,
    })).filter(x => x.amount !== null && x.amount > 0)
    : null;
  const date = v => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) && v.length <= 30 ? v : null);
  const keys = CRM_COLUMNS.filter(c => !c.required).map(c => c.key);
  return {
    version: CRM_METRICS_VERSION,
    computedAt: date(m.computedAt) || new Date().toISOString(),
    exportDate: date(m.exportDate),
    rows: { total: num(m.rows?.total, { int: true, max: 1e7 }) ?? 0, used: num(m.rows?.used, { int: true, max: 1e7 }) ?? 0, skipped: num(m.rows?.skipped, { int: true, max: 1e7 }) ?? 0 },
    missingOptional: Array.isArray(m.missingOptional) ? m.missingOptional.filter(k => keys.includes(k)) : [],
    stageOrder,
    estimated: { stageConversion: m.estimated?.stageConversion === true, stalled: m.estimated?.stalled === true },
    sellerLinks,
    total,
    byPerson,
    unlinked: m.unlinked ? cleanBucket(m.unlinked, stageOrder.length) : null,
    byChannel,
    lossReasons,
    byIndustry: segmentMap(m.byIndustry), bySize: segmentMap(m.bySize), bySegment, byPersonChannel,
    topOpen,
  };
}
